import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { PageShell, BackHeader, Callout, Panel, Skeleton, Icon, Text, Toast } from '../../../components';
import { ROUTE } from '../../../routes';
import { useFeatureGate } from '../../../hooks/useFeatureGate';
import type { MailboxRecord } from '../../../mock-data/mailbox-records';
import type { MailClientSettings } from '../api/client';
import { mailboxService } from '../api/service';
import { MAILBOX_SOURCE } from '../api/source';
import { stashFirstRun, takeFirstRun } from '../firstRun';
import { generateMailboxPassword } from '../password';
import { SubPage, Contents } from '../../settings/settings.styles';
import { useMailboxes } from '../useMailboxes';
import { useMailboxEligibility } from '../useMailboxEligibility';
import { readyUsage, type MailboxUsageRead } from '../useMailboxUsage';
import { useOpenWebmail } from '../webmail';
import { mailboxErrorText, takeMailboxError } from '../errors';
import {
  formatMailboxStorage,
  mailboxDomainBlocksActions,
  mailboxDomainAwaitingSetup,
  mailboxIsShown,
  mailboxPrimaryAction,
  mailboxShowsStatusPill,
  mailboxStorageRatio,
  mailboxStorageTone,
  mailboxTimeAgoLabel,
  mailboxMessagesLine,
} from '../state';
import { ClientGuide, CredentialBlock, Tooltip } from './components';
import { DeleteMailboxDialog, LoadFailedNotice, MailboxUnavailableNotice, RotatePasswordDialog } from '../components';
import { useToast } from '../../../hooks/useToast';
import {
  Body,
  Header,
  HeaderLeft,
  Address,
  HeaderActions,
  WebmailButton,
  TrashButton,
  StatusPill,
  Stats,
  Stat,
  PlanStat,
  StatLabel,
  StatValue,
  StatSub,
  StatLine,
  Bar,
  BarFill,
  Section,
  SectionHeading,
  SkelStack,
  PILL_TONES,
} from './detail.styles';

export type MailboxDetailProps = {
  styleText?: string;
  className?: string;
};

/** The section this page belongs to; back and the unknown-id bounce both land here. */
const SECTION_ROUTE = ROUTE.settingsSection('mailbox');

/**
 * One mailbox's page. With NO mail service in this environment (`MAILBOX_SOURCE === 'unavailable'`)
 * it says so under its back header, behind the same flag gate, and reads nothing: no fixture mailbox
 * can appear here either. `MAILBOX_SOURCE` is fixed for the page's lifetime.
 */
export const MailboxDetail = (props: MailboxDetailProps): React.ReactElement =>
  MAILBOX_SOURCE === 'unavailable' ? <MailboxDetailUnavailable {...props} /> : <MailboxDetailLive {...props} />;

const MailboxDetailUnavailable = ({ styleText = '', className = '' }: MailboxDetailProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isEnabled } = useFeatureGate();
  const onBack = React.useCallback(() => navigate(SECTION_ROUTE), [navigate]);
  if (!isEnabled('mailbox')) return <Navigate to={ROUTE.settings()} replace />;
  return (
    <PageShell styleText={styleText} className={className}>
      <SubPage>
        <BackHeader backLabel={t('Back to Mailbox')} onBack={onBack} />
        <Contents>
          <MailboxUnavailableNotice />
        </Contents>
      </SubPage>
    </PageShell>
  );
};

/**
 * The live page's gate. A direct hit while the `mailbox` flag is off (a stale link in production), or
 * once ows-mail has not said the domain may have mail (`useMailboxEligibility`), bounces to the Settings
 * index, exactly as the section does. Until that answer is in, the page shows nothing and reads
 * nothing, so it never appears and then goes.
 */
const MailboxDetailLive = (props: MailboxDetailProps): React.ReactElement | null => {
  const { isEnabled } = useFeatureGate();
  const eligibility = useMailboxEligibility();
  if (!isEnabled('mailbox') || eligibility === 'not_eligible' || eligibility === 'unknown') {
    return <Navigate to={ROUTE.settings()} replace />;
  }
  return eligibility === 'eligible' ? <MailboxDetailBody {...props} /> : null;
};

/**
 * One mailbox's Setup Mailbox page (Mailbox), mounted at
 * `ROUTE.mailboxDetail(email)` as its OWN route, so refresh and deep links land
 * on the mailbox. It is the only place a password can be reset, the only place the server settings
 * live, and the only way into webmail from a card that is not `Webmail`.
 *
 * Top to bottom: the back band (outside the panel), the header (address, ONE primary action chosen
 * by `mailboxPrimaryAction`, from the provider status as the tile's Webmail is, then the trash on a paid mailbox), the
 * status line (which explains and never carries a button), the credential block, three stats and the client guide.
 * The header's Webmail link is the one way to webmail: a second link further down said the same
 * thing under a different name.
 *
 * State comes from `useMailboxes`, the same per-brand hook the grid mounts: this route holds
 * its own reducer, so the plaintext password lives only while the page does, and `passwordSetAt`
 * round-trips through the store. Gates: the `mailbox` flag and ows-mail's eligibility answer
 * (`MailboxDetailLive`, before this mounts) and the address (unknown once the brand's mailboxes have
 * loaded: the section). Usage (storage,
 * messages, last received) is read separately from the list, as the API returns it, and is the
 * only read that asks for "last received": this is the only screen that shows it.
 *
 * Shares no code with Scenario A or B by design; what looks copied from their client-setup blocks
 * was written here from their shape.
 */
const MailboxDetailBody = ({ styleText = '', className = '' }: MailboxDetailProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // React Router hands the param back decoded, so this is the address `ROUTE.mailboxDetail` encoded.
  const { email: emailParam } = useParams<{ email: string }>();
  const email = emailParam?.toLowerCase();
  const { isEnabled } = useFeatureGate();
  // The page re-reads its domain while it is being provisioned, so its WAITING state lifts on its own
  // when the domain turns ready, without the operator leaving and coming back.
  const { state, dispatch, domain, brandId, loading, usageOf, requestUsage, loadError, loadFailed, reloadList } =
    useMailboxes({ pollDomain: true });

  // A mailbox the operator does not see (support@ while `mailbox-support` is off) is not found
  // here either: a direct URL takes the unknown-address bounce to the section, and its usage is never read.
  const mailbox = state.mailboxes.find((m) => m.email === email && mailboxIsShown(m, isEnabled('mailbox-support')));
  const secret = email ? state.secrets[email] : undefined;
  const usage: MailboxUsageRead = mailbox ? usageOf(mailbox) : { status: 'loading' };
  // This page reads its own usage, once, when its body mounts (the list has landed and the address
  // is known), and is the only read that asks for "last received" (it costs the service a third
  // upstream call; this is the only screen that shows it). Re-renders re-run this, and the cache
  // makes every repeat a no-op.
  React.useEffect(() => {
    if (!loading && mailbox) requestUsage(mailbox, { lastReceived: true });
  }, [loading, mailbox, requestUsage]);

  // Back: a real history pop when there is an in-app entry to pop (so the route-level transition
  // plays in reverse), else straight to the section, so a hard-refreshed deep link with no history
  // still lands somewhere useful. Same shape as the area's Settings back hook, different fallback.
  const onBack = React.useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) navigate(-1);
    else navigate(SECTION_ROUTE);
  }, [navigate]);

  const { toast, show, dismiss } = useToast();

  // INERT: a mailbox the provider has deleted, or any mailbox on a domain whose setup failed, offers
  // nothing to press here either (the section's tile already offers nothing): the service would
  // refuse a rotation, a sign-in or a delete for it. Reached by a deep link or a page left open.
  const domainFailed = mailboxDomainBlocksActions(state.mailDomain);
  const inert = domainFailed || mailbox?.status === 'deleted';
  // WAITING: the domain is still being set up, so the mailbox cannot receive yet: the same rule the
  // grid's notice and cards read (`mailboxDomainAwaitingSetup`; support@'s Pending is that rule too).
  // Reached by a bookmark or history, the page must not undo what the grid's card withholds: Webmail
  // is disabled, the credentials and the client guide are held back, and the page says why.
  // A deleted mailbox never waits: it will never be set up.
  const waiting =
    mailbox !== undefined && mailbox.status !== 'deleted' && mailboxDomainAwaitingSetup(state.mailDomain, state.mailboxes);

  // A password issued on THIS page (a change, or the first generate), through the SAME password
  // endpoint the create form's rotation uses: the plaintext goes into the reducer's transient map,
  // and the row shows itself (dots, eye, copy). A toast confirms it; a refusal says the service's
  // own words. `rotationGen` drops an answer that lands after the page moved on (another address,
  // another brand, unmounted).
  const rotationGen = React.useRef(0);
  React.useEffect(() => {
    rotationGen.current += 1;
    const gen = rotationGen;
    return () => {
      gen.current += 1;
    };
  }, [email, brandId]);
  const [resetting, setResetting] = React.useState(false);
  const issuePassword = React.useCallback(
    (address: string, password: string, confirmToast: boolean) => {
      const gen = rotationGen.current;
      setResetting(true);
      void mailboxService.rotatePassword(domain, address, password).then(
        ({ password }) => {
          // The old password is already dead: if the page has moved on, hold the new one for this
          // mailbox's next visit rather than drop it (it exists nowhere else).
          if (gen !== rotationGen.current) {
            stashFirstRun(address, password);
            return;
          }
          setResetting(false);
          dispatch({ type: 'passwordIssued', email: address, setAt: new Date().toISOString() });
          dispatch({ type: 'setSecret', email: address, value: password });
          // A toast, not a line: the row already changed in front of the operator.
          if (confirmToast) show(t('Password changed.'));
        },
        (rejection: unknown) => {
          if (gen !== rotationGen.current) return;
          setResetting(false);
          show(mailboxErrorText(takeMailboxError(rejection), t, t('The password could not be changed. Try again in a moment.')));
        },
      );
    },
    [dispatch, show, t, domain],
  );

  // Reset: Change password asks first, because a rotation signs the owner out of EVERY mail client at
  // once (there are no per-device passwords), and the confirmation says so. The old password stops
  // working when the new one lands. `resetting` holds while the round trip is in flight, so a
  // second click cannot issue a second password; another address or brand clears it.
  const [rotateOpen, setRotateOpen] = React.useState(false);
  React.useEffect(() => {
    setResetting(false);
    setRotateOpen(false);
  }, [email, brandId]);
  const onReset = React.useCallback(() => {
    if (mailbox && !resetting && !inert) setRotateOpen(true);
  }, [mailbox, resetting, inert]);
  const onRotateCancel = React.useCallback(() => setRotateOpen(false), []);
  // Rotate to the password chosen in the dialog (generated there, or typed).
  const onRotateConfirm = React.useCallback(
    (password: string) => {
      setRotateOpen(false);
      if (mailbox && !resetting && !inert) issuePassword(mailbox.email, password, true);
    },
    [mailbox, resetting, inert, issuePassword],
  );

  // First password on a never-set mailbox: nothing to invalidate, so no confirmation. Generated HERE
  // rather than left to the service, so the browser is the one generator on every path.
  const onGenerate = React.useCallback(() => {
    if (mailbox && !resetting && !inert) issuePassword(mailbox.email, generateMailboxPassword(), false);
  }, [mailbox, resetting, inert, issuePassword]);

  // FIRST RUN: the mailbox was created this session and its plaintext is still in hand (see
  // `firstRun.ts`). Taken once, on arrival, into this route's own transient secrets, so the
  // password row can reveal and copy it. Nothing else marks the visit: the header carries no
  // confirmation pill (Figma 56:2437 is the address, one action and the trash, in every state).
  // Taken only once the mailboxes have LANDED: a hydrate clears the transient secrets, and the live
  // source hydrates when the service answers, after mount, so a take on mount would be wiped.
  const firstRunReady = !loading && mailbox !== undefined;
  React.useEffect(() => {
    if (!email || !firstRunReady) return;
    const held = takeFirstRun(email);
    if (held !== null) dispatch({ type: 'setSecret', email, value: held });
  }, [email, dispatch, firstRunReady]);

  // Webmail: a fresh SSO call on every click, never before one.
  const openWebmail = useOpenWebmail(show, domain);
  const onWebmail = React.useCallback(() => {
    if (mailbox && !waiting) openWebmail(mailbox);
  }, [mailbox, waiting, openWebmail]);

  // Delete, from the header's trash (a PAID mailbox only, any state): the DELETE dialog, then
  // `remove`, then the section, with `replace` so Back does not return to a mailbox that is gone.
  // The unknown-id guard would bounce eventually, but it would flash the skeleton first and leave
  // a dead history entry. The toast is shown by the section, which reads the flag once.
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [leaving, setLeaving] = React.useState(false);
  // Paid mailboxes only (the service refuses both free ones, support@ and the owner's first, with a 409); gated here as
  // well as on the button.
  const onDelete = React.useCallback(() => {
    if (mailbox?.billing === 'paid') setDeleteOpen(true);
  }, [mailbox]);
  const onDeleteCancel = React.useCallback(() => setDeleteOpen(false), []);
  // The row goes (and the page leaves) only once the service has deleted the mailbox; a refusal
  // keeps the operator here and says why (a `404` counts as deleted: see the service adapter).
  const onDeleteConfirm = React.useCallback(
    (address: string) => {
      setDeleteOpen(false);
      const gen = rotationGen.current;
      void mailboxService.deleteMailbox(domain, address).then(
        () => {
          if (gen !== rotationGen.current) return;
          dispatch({ type: 'remove', email: address });
          setLeaving(true);
        },
        (rejection: unknown) => {
          if (gen !== rotationGen.current) return;
          show(mailboxErrorText(takeMailboxError(rejection), t, t('The mailbox could not be deleted. Try again in a moment.')));
        },
      );
    },
    [dispatch, domain, show, t],
  );

  // The client-setup table's hosts and ports, from the service on every visit (they arrive with
  // the password only at create and rotate, and this page is also reached later). The mock source
  // has none, and the table keeps its fixed rows there. Not asked for a mailbox with nothing to set up.
  const [settings, setSettings] = React.useState<MailClientSettings | undefined>(undefined);
  const settingsFor = !loading && mailbox && !inert && !waiting ? mailbox.email : null;
  React.useEffect(() => {
    setSettings(undefined);
    if (settingsFor === null) return undefined;
    let cancelled = false;
    mailboxService.fetchAccountSettings(domain, settingsFor).then(
      (answer) => {
        if (!cancelled) setSettings(answer);
      },
      (rejection: unknown) => {
        // The table then keeps its fixed rows; `takeMailboxError` logs a 502's upstream.
        takeMailboxError(rejection);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [domain, settingsFor]);
  // Leave only once the removal has COMMITTED: navigating in the same tick as the dispatch would
  // unmount this route before the hook's persist effect saw the new state, and the section would
  // re-hydrate the seed with the mailbox back in it. The persist effect runs first in this commit
  // (the hook is called above), so the record on disk is already without the mailbox here.
  React.useEffect(() => {
    if (leaving && !mailbox) navigate(SECTION_ROUTE, { replace: true, state: { deleted: true } });
  }, [leaving, mailbox, navigate]);

  // An unknown address (a stale link, a deleted mailbox) bounces to the SECTION, not the index. Only
  // decided once the brand's mailboxes have loaded: on a hard refresh the first render has no brand
  // yet and an empty list, and bouncing then would break every deep link. A delete made HERE is
  // handled by the effect above instead (it carries the toast flag).
  // A live read that FAILED is not an unknown address: the page stays and says so, rather than
  // bouncing to the section with no explanation (and nothing on it to press).
  if (!loading && loadFailed) {
    return (
      <PageShell styleText={styleText} className={className}>
        <SubPage>
          <BackHeader backLabel={t('Back to Mailbox')} onBack={onBack} />
          <Contents>
            <div data-testid="mailbox-read-failed-notice">
              {/* A session that ended (401), a domain that is not this owner's (403, the service's
                  own words), or the service's words / ours for anything else. */}
              <LoadFailedNotice error={loadError} onRetry={reloadList} />
            </div>
          </Contents>
        </SubPage>
      </PageShell>
    );
  }
  if (!loading && !mailbox && !leaving) {
    return <Navigate to={SECTION_ROUTE} replace />;
  }

  return (
    <PageShell styleText={styleText} className={className}>
      <SubPage>
        {/* Titleless band: the page shows its own heading (the address) below, per BackHeader's
            own guidance; the back button is named for assistive tech instead. */}
        <BackHeader backLabel={t('Back to Mailbox')} onBack={onBack} />
        <Contents>
          {/* The domain's failure, as the section shows it: live state, no dismiss. It is why
              every action below is gone. */}
          {!loading && domainFailed ? (
            <div data-testid="mailbox-domain-failed-notice">
              {state.mailDomain?.failureReason ? (
                <Callout tone="danger" title="Mail setup failed" text={state.mailDomain.failureReason} />
              ) : (
                <Callout tone="danger" title="Mail setup failed" text="The provider did not say why. Contact support." />
              )}
            </div>
          ) : null}
          <Panel>
            {loading || !mailbox ? (
              <SkelStack data-testid="mailbox-detail-loading">
                <Skeleton block height={28} radius={8} />
                <Skeleton block height={96} radius={12} />
                <Skeleton block height={84} radius={12} />
              </SkelStack>
            ) : (
              <DetailBody
                mailbox={mailbox}
                usage={usage}
                inert={inert}
                waiting={waiting}
                domain={domain}
                secret={secret}
                settings={settings}
                language={i18n.language}
                onGenerate={onGenerate}
                onReset={onReset}
                resetting={resetting}
                onWebmail={onWebmail}
                onDelete={onDelete}
              />
            )}
          </Panel>
        </Contents>
      </SubPage>

      <RotatePasswordDialog
        open={rotateOpen}
        address={mailbox?.email ?? ''}
        onCancel={onRotateCancel}
        onConfirm={onRotateConfirm}
      />
      <DeleteMailboxDialog
        open={deleteOpen}
        mailbox={mailbox ?? null}
        usage={readyUsage(usage)}
        onCancel={onDeleteCancel}
        onConfirm={onDeleteConfirm}
      />
      <Toast toast={toast} onDismiss={dismiss} />
    </PageShell>
  );
};

type DetailBodyProps = {
  mailbox: MailboxRecord;
  /** Read separately from the list, on mount (see `useMailboxUsage`). */
  usage: MailboxUsageRead;
  /** Nothing to press: a deleted mailbox, or its domain's setup failed. */
  inert: boolean;
  /** The domain is still being provisioned: no Webmail, no credentials, no client guide yet. */
  waiting: boolean;
  domain: string;
  secret: string | undefined;
  /** The service's mail client settings, when it has answered with them. */
  settings: MailClientSettings | undefined;
  language: string;
  onGenerate: () => void;
  onReset: () => void;
  resetting: boolean;
  onWebmail: () => void;
  onDelete: () => void;
};

/** The loaded page body, split out so the hooks above stay unconditional. */
const DetailBody = ({
  mailbox,
  usage: usageRead,
  inert,
  waiting,
  domain,
  secret,
  settings,
  language,
  onGenerate,
  onReset,
  resetting,
  onWebmail,
  onDelete,
}: DetailBodyProps): React.ReactElement => {
  const { t } = useTranslation();
  const address = mailbox.email;
  // Until the read answers, each figure holds a skeleton; a dash means it answered with nothing.
  const usageLoading = usageRead.status === 'loading';
  const usage = readyUsage(usageRead);
  const messagesLine = mailboxMessagesLine(usage ?? undefined);
  const action = mailboxPrimaryAction(mailbox);
  // NO PRICE ON AN EXISTING MAILBOX. A paid one was charged once, at its creation, at whatever the
  // price was then, and nothing records that amount: the service's price answers what the NEXT
  // paid mailbox costs and changes without recalculating anything. Showing it here would state a
  // figure this mailbox may never have been charged. The slot says it all.
  const paid = mailbox.billing === 'paid';

  // The pill beside the address: a PROVIDER status (suspended, deleted), with the tile's precedence:
  // it says why the mailbox will not sign in. Billing never gets a pill. Healthy shows nothing.
  const pill = mailboxShowsStatusPill(mailbox)
    ? mailbox.status === 'deleted'
      ? { ...PILL_TONES.deleted, label: t('Deleted', { context: 'mailbox' }) }
      : { ...PILL_TONES.suspended, label: t('Suspended') }
    : null;

  return (
    <Body data-testid="mailbox-detail" data-status={mailbox.status}>
      {/* THE HEADER'S GRAMMAR, written down because it has been reorganised three times. The page
          has three shapes and each does ONE job:

            | Shape                        | Says                         | Example                        |
            | pill, beside the address     | a provider status, if any    | Suspended / Deleted            |
            | info glyph, on the Plan card | the plan                     | free, or paid once             |
            | toast                        | something that just happened | Password changed.              |

          There is NO status sentence under the header in any state, no first-run pill and no banner
          under the credential block (Figma 56:2434 / 56:2203): the Plan card carries the plan, the
          action button acts. Left group: the address (then a pill only for the statuses above).
          Right group: Webmail (or nothing) and the trash, nothing else. */}
      <Header>
        <HeaderLeft>
          <Address>{address}</Address>
          {/* No confirmation pill on a first run and none for a healthy mailbox: only the provider
              statuses that need naming (suspended, deleted). */}
          {pill ? (
            <StatusPill $bg={pill.bg} $fg={pill.fg} data-testid="mailbox-status-pill">
              {pill.label}
            </StatusPill>
          ) : null}
        </HeaderLeft>
        <HeaderActions>
          {action.kind === 'open-webmail' || inert ? (
            // Disabled when INERT (a deleted mailbox, whose SSO call answers 409, or a failed
            // domain): the button says there is no webmail rather than failing on the click.
            <WebmailButton type="button" onClick={onWebmail} disabled={inert || waiting}>
              <Text text="Webmail" tag="span" />
              <Icon name="external-link" size={16} color="currentColor" />
            </WebmailButton>
          ) : null}
          {/* Delete, as an icon-only button in the header slot: a one-item menu was worse than the
              item. Offered on a PAID mailbox only (both free ones are refused by the
              service), in every state but an INERT one, and never the page's primary verb; it opens
              the DELETE-in-capitals dialog, so one click is still not destructive. */}
          {inert || mailbox.billing !== 'paid' ? null : (
            <TrashButton
              type="button"
              aria-label={t('Delete mailbox')}
              onClick={onDelete}
              data-testid="mailbox-delete-button"
            >
              <Icon name="trash" size={18} color="currentColor" />
            </TrashButton>
          )}
        </HeaderActions>
      </Header>

      {waiting ? (
        // Says why Webmail and the client guide are held back: a disabled control with no stated reason
        // is its own bug.
        <div data-testid="mailbox-pending-line">
          <Callout tone="info" text="This mailbox can be set up once its domain is ready to receive mail." />
        </div>
      ) : null}
      {/* The credentials wait with the client guide, EXCEPT a password still held to be shown once (the
          first run after a create, which is exactly when the domain is still being set up): it is the
          one thing that cannot be recovered, so it shows, once, as it always has. Once seen, the block
          waits too until the domain is ready. */}
      {waiting && secret === undefined ? null : (
        <CredentialBlock
          address={address}
          secret={secret}
          passwordSetAt={mailbox.passwordSetAt}
          onGenerate={onGenerate}
          onReset={onReset}
          resetting={resetting}
          disabled={inert}
        />
      )}

      <Stats>
        <Stat>
          <StatLabel text="Storage" tag="span" />
          <StatValue data-testid="mailbox-storage-value">
            {usageLoading ? (
              <Skeleton width={120} height={18} />
            ) : usage ? (
              formatMailboxStorage(usage.bytesUsed, usage.bytesMax, t)
            ) : (
              // No usage to read, or the read failed: a dash, the area's no-value marker. Not
              // "0 KB": nothing was read.
              '–'
            )}
          </StatValue>
          {/* ABSENT `bytesMax` MEANS UNKNOWN, NOT ZERO: no ceiling reported, so no bar to fill. */}
          {usage && usage.bytesMax !== undefined ? (
            <Bar
              role="progressbar"
              aria-label={t('Storage')}
              aria-valuemin={0}
              aria-valuemax={usage.bytesMax}
              aria-valuenow={Math.min(usage.bytesUsed, usage.bytesMax)}
            >
              <BarFill
                $ratio={mailboxStorageRatio(usage.bytesUsed, usage.bytesMax)}
                $tone={mailboxStorageTone(usage.bytesUsed, usage.bytesMax)}
              />
            </Bar>
          ) : null}
          {/* The read failed: the service's own message, verbatim, under the dash. It is written for
              an operator; a generic line in its place would hide what went wrong. */}
          {usageRead.status === 'failed' && mailboxErrorText(usageRead.error, t, '') ? (
            <StatSub text={mailboxErrorText(usageRead.error, t, '')} tag="span" />
          ) : null}
        </Stat>
        <Stat data-testid="mailbox-messages-stat">
          <StatLabel text="Messages" tag="span" />
          <StatValue data-testid="mailbox-messages-value">
            {usageLoading ? <Skeleton width={64} height={18} /> : usage ? usage.messagesUsed.toLocaleString(language) : '–'}
          </StatValue>
          {/* ONE line under the count, and only what has something to report: when mail last
              arrived, and that unread mail is waiting. Each half shows only when it says something
              (an absent `lastReceivedAt`, INBOX-only and moving backwards when mail is filed, proves
              nothing; `unreadMail` false or absent is not news), and with neither the line is absent
              and the card is just the number. `unreadMail` is a boolean, not a count, by the
              service's design, so the second half says that mail is waiting, not how much.
              DELIBERATELY OFF THE CARD: the sends against the mailbox's own cap (`smtpSent` /
              `smtpMax`). They are still read with the usage, and they are still the only place a
              mailbox's own send cap would surface: a new mailbox is often capped at 25 against a
              domain's 500, and an operator who hits that now has nothing on screen explaining why.
              A deliberate trade for a quieter card, not an oversight. */}
          {messagesLine ? (
            <StatLine data-testid="mailbox-messages-line">
              {messagesLine.lastReceivedAt ? (
                <Text
                  text="Last received {{ago}}"
                  placeholders={{ ago: mailboxTimeAgoLabel(messagesLine.lastReceivedAt, t) }}
                  tag="span"
                />
              ) : null}
              {messagesLine.lastReceivedAt && messagesLine.unread ? <span aria-hidden="true">·</span> : null}
              {messagesLine.unread ? <Text text="Unread mail waiting" tag="span" /> : null}
            </StatLine>
          ) : null}
        </Stat>
        <PlanStat data-testid="mailbox-plan-stat">
          <StatLabel text="Plan" tag="span" />
          {/* The info glyph, in the card's corner: about THE PLAN (free, or paid once), not about the
              mailbox in general. Provider statuses (suspended, deleted) are not billing states, so
              they read as their plan; their pills already carry them. */}
          <Tooltip label={t('About this plan')} corner testId="mailbox-plan-info">
            {paid ? (
              <Text text="Charged once, when this mailbox was created. Nothing is charged after that." tag="span" />
            ) : (
              <Text text="Included with your brand, free forever. It is never charged." tag="span" />
            )}
          </Tooltip>
          {paid ? (
            <StatValue>
              <Text text="Paid" tag="span" />
            </StatValue>
          ) : (
            <StatValue>
              <Text text="Free" tag="span" />
            </StatValue>
          )}
          {paid ? (
            <StatSub text="Charged once, at creation" tag="span" />
          ) : (
            <StatSub text="Included with your brand" tag="span" />
          )}
        </PlanStat>
      </Stats>

      {waiting ? null : (
        <Section>
          <SectionHeading text="Set it up in your mail app" tag="h3" />
          <ClientGuide domain={domain} settings={settings} />
        </Section>
      )}
    </Body>
  );
};

export default MailboxDetail;
